// src/modulos/Parametros/NCMRegisterPage.js
import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from '../Registers/Registers.module.css';
import ncmStyles from './NCMRegisterPage.module.css';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { BsFiletypeJson } from "react-icons/bs";
import {
    PageHeader,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    FormModal,
    Toast,
    ConfirmModal
} from '../../components/common';

const NCM_COLLECTION = 'parameters/data/ncm';
const UNIT_OF_MEASURE_COLLECTION = 'parameters/data/unidades-medida';
const ITEMS_PER_PAGE = 10;

const CONTROLE_OPTIONS = ['Nenhum', 'Polícia Federal', 'Polícia Civil', 'Exército'];
const UF_OPTIONS = ['EX', 'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const ST_OPTIONS = [
    { code: '00', description: 'Tributada integralmente' },
    { code: '10', description: 'Tributada e com cobrança de ICMS por ST' },
    { code: '20', description: 'Com redução de base de cálculo' },
    { code: '40', description: 'Isenta' },
    { code: '41', description: 'Não tributada' },
    { code: '50', description: 'Suspensão' },
    { code: '60', description: 'ICMS cobrado anteriormente por ST' },
    { code: '300', description: 'Imune' }
];

const SITUACAO_TRIBUTARIA_OPTIONS = [
    { code: '00', description: 'Tributada integralmente' },
    { code: '10', description: 'Tributada e com cobrança de ICMS por ST' },
    { code: '20', description: 'Com redução de base de cálculo' },
    { code: '30', description: 'Isenta / Não tributada' },
    { code: '40', description: 'Suspensão' },
    { code: '41', description: 'Não tributada' },
    { code: '50', description: 'Substituição Tributária' },
    { code: '51', description: 'Diferimento' },
    { code: '60', description: 'ICMS cobrado anteriormente por ST' },
    { code: '70', description: 'Com redução da base de cálculo e cobrança de ICMS por ST' },
    { code: '90', description: 'Outras' },
    { code: '300', description: 'Imune' }
];

const MODALIDADE_BASE_CALCULO_OPTIONS = [
    { code: '0', description: 'Margem Valor Agregado (%)' },
    { code: '1', description: 'Pauta (Valor)' },
    { code: '2', description: 'Preço Tabelado Máximo (Valor)' },
    { code: '3', description: 'Valor da Operação' }
];

const MODALIDADE_BASE_CALCULO_ST_OPTIONS = [
    { code: '0', description: 'Preço tabelado ou máximo sugerido' },
    { code: '1', description: 'Lista negativa (valor)' },
    { code: '2', description: 'Lista positiva (valor)' },
    { code: '3', description: 'Lista neutra (valor)' },
    { code: '4', description: 'Margem Valor Agregado (%)' },
    { code: '5', description: 'Pauta (valor)' }
];

const GENERO_OPTIONS = [
    { value: '', label: '- Selecione -' },
    { value: 'alimenticio', label: 'Alimentício' },
    { value: 'bebidas', label: 'Bebidas' },
    { value: 'eletronicos', label: 'Eletrônicos' },
    { value: 'farmaceutico', label: 'Farmacêutico' },
    { value: 'metalurgico', label: 'Metalúrgico' },
    { value: 'quimico', label: 'Químico' },
    { value: 'textil', label: 'Têxtil' },
    { value: 'veiculos', label: 'Veículos' },
    { value: 'outros', label: 'Outros' }
];

// ─── Import Modal ────────────────────────────────────────────────────────────

const ImportModal = ({ onClose, existingCodes, addDocument, t }) => {
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };

    const handleImport = async () => {
        if (!file) return;
        setImporting(true);
        try {
            const text = await file.text();
            const data = JSON.parse(text);
            const ncmData = data.NCMs || data;
            const items = Array.isArray(ncmData) ? ncmData : Object.values(ncmData);
            let imported = 0;
            let skipped = 0;

            setProgress({ current: 0, total: items.length });

            for (const item of items) {
                if (item.ncmCode && !existingCodes.has(item.ncmCode)) {
                    await addDocument({
                        ncmCode: item.ncmCode,
                        ncmDescription: item.ncmDescription || item.descricao || '',
                        control: item.control || 'Nenhum',
                        ...item,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    });
                    imported++;
                } else {
                    skipped++;
                }
                setProgress(prev => ({ ...prev, current: prev.current + 1 }));
            }

            alert(t('pages.ncm.importSuccess'));
            onClose();
        } catch (error) {
            console.error('Import error:', error);
            alert(t('pages.ncm.importError'));
        } finally {
            setImporting(false);
            setProgress({ current: 0, total: 0 });
        }
    };

    return (
        <FormModal
            isOpen={true}
            onClose={onClose}
            onSubmit={handleImport}
            title={t('pages.ncm.importJson')}
            isLoading={importing}
            submitText={importing ? t('pages.ncm.importing') : t('pages.ncm.importJson')}
            maxWidth="500px"
        >
            <div className={styles.formGroup}>
                <label>Arquivo JSON da Receita Federal</label>
                <input type="file" accept=".json" onChange={handleFileChange} className={styles.input} disabled={importing} />
                <small style={{ color: 'var(--text-secondary)' }}>Selecione um arquivo JSON com a lista de NCMs</small>
            </div>
            {importing && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span>Importando...</span>
                        <span>{progress.current} / {progress.total}</span>
                    </div>
                    <div style={{ height: 6, background: '#eee', borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`, background: 'var(--color-primary, #4a90e2)', transition: 'width 0.3s' }} />
                    </div>
                </div>
            )}
        </FormModal>
    );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const NCMRegisterPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('cadastro_de_ncm');
    const { data: ncmList, loading, error, addDocument, updateDocument, deleteDocument } = useFirestore(NCM_COLLECTION);
    const { data: unitOfMeasures } = useFirestore(UNIT_OF_MEASURE_COLLECTION);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [formLoading, setFormLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('geral');
    const [toasts, setToasts] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Form state - Dados Gerais
    const [ncmCode, setNcmCode] = useState('');
    const [ncmDescription, setNcmDescription] = useState('');
    const [control, setControl] = useState('Nenhum');
    const [unitOfMeasureId, setUnitOfMeasureId] = useState('');
    const [exportUnitOfMeasureId, setExportUnitOfMeasureId] = useState('');
    const [originDestinyRules, setOriginDestinyRules] = useState([]);

    // Form state - Tributação ICMS
    const [situacaoTributaria, setSituacaoTributaria] = useState('00');
    const [modalidadeBaseCalculo, setModalidadeBaseCalculo] = useState('3');
    const [modalidadeBaseCalculoST, setModalidadeBaseCalculoST] = useState('4');
    const [inscricaoEstadualST, setInscricaoEstadualST] = useState('');
    const [aliquotaIcmsProprio, setAliquotaIcmsProprio] = useState('');
    const [aliquotaIVA, setAliquotaIVA] = useState('');
    const [aliquotaIVAExcecao, setAliquotaIVAExcecao] = useState('');
    const [validadeIVA, setValidadeIVA] = useState('');
    const [aliquotaIVASimplesNacional, setAliquotaIVASimplesNacional] = useState('');
    const [aliquotaIVAExcecaoSimplesNacional, setAliquotaIVAExcecaoSimplesNacional] = useState('');
    const [aliquotaAdicaoBase, setAliquotaAdicaoBase] = useState('');
    const [aliquotaReducaoBase, setAliquotaReducaoBase] = useState('');
    const [aliquotaIcmsSTOutraUF, setAliquotaIcmsSTOutraUF] = useState('');
    const [aliquotaIcmsFreteDiferenciado, setAliquotaIcmsFreteDiferenciado] = useState('');
    const [aliquotaIcmsNaoContribuinte, setAliquotaIcmsNaoContribuinte] = useState('');
    const [convenioSubstituicaoTributaria, setConvenioSubstituicaoTributaria] = useState(false);
    const [aliquotaIcmsDiferencialST, setAliquotaIcmsDiferencialST] = useState('');
    const [aliquotaReducaoBaseIcmsST, setAliquotaReducaoBaseIcmsST] = useState('');
    const [percentualFundoPobreza, setPercentualFundoPobreza] = useState('');

    // Form state - Tributação Federal
    const [aliquotaIpiSaida, setAliquotaIpiSaida] = useState('');
    const [aliquotaIpiEntrada, setAliquotaIpiEntrada] = useState('');
    const [aliquotaExcecaoIpiSaida, setAliquotaExcecaoIpiSaida] = useState('');
    const [aliquotaExcecaoIpiEntrada, setAliquotaExcecaoIpiEntrada] = useState('');
    const [aliquotaII, setAliquotaII] = useState('');
    const [aliquotaCofinsSaida, setAliquotaCofinsSaida] = useState('');
    const [aliquotaCofinsEntrada, setAliquotaCofinsEntrada] = useState('');
    const [aliquotaPISSaida, setAliquotaPISSaida] = useState('');
    const [aliquotaPISEntrada, setAliquotaPISEntrada] = useState('');
    const [aliquotaCBS, setAliquotaCBS] = useState('');
    const [aliquotaIBS, setAliquotaIBS] = useState('');
    const [aliquotaIS, setAliquotaIS] = useState('');
    const [codigoEspecificacaoST, setCodigoEspecificacaoST] = useState('');
    const [codigoExcecaoEspecificacaoST, setCodigoExcecaoEspecificacaoST] = useState('');
    const [genero, setGenero] = useState('');
    const [mensagemLegal, setMensagemLegal] = useState('');

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    };

    const existingCodes = useMemo(() => new Set(ncmList.map(i => i.ncmCode)), [ncmList]);

    const filteredNCM = useMemo(() => {
        const filtered = ncmList.filter(item =>
            item.ncmCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.ncmDescription?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return filtered.sort((a, b) =>
            (a.ncmCode || "").localeCompare((b.ncmCode || ""), undefined, { numeric: true, sensitivity: 'base' })
        );
    }, [ncmList, searchTerm]);

    const totalPages = Math.ceil(filteredNCM.length / ITEMS_PER_PAGE);
    const paginatedNCM = filteredNCM.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            setNcmCode(item.ncmCode || '');
            setNcmDescription(item.ncmDescription || '');
            setControl(item.control || 'Nenhum');
            setUnitOfMeasureId(item.unitOfMeasureId || '');
            setExportUnitOfMeasureId(item.exportUnitOfMeasureId || '');
            setOriginDestinyRules(item.originDestinyRules || []);
            setSituacaoTributaria(item.situacaoTributaria || '00');
            setModalidadeBaseCalculo(item.modalidadeBaseCalculo || '3');
            setModalidadeBaseCalculoST(item.modalidadeBaseCalculoST || '4');
            setInscricaoEstadualST(item.inscricaoEstadualST || '');
            setAliquotaIcmsProprio(item.aliquotaIcmsProprio?.toString() || '');
            setAliquotaIVA(item.aliquotaIVA?.toString() || '');
            setAliquotaIVAExcecao(item.aliquotaIVAExcecao?.toString() || '');
            setValidadeIVA(item.validadeIVA || '');
            setAliquotaIVASimplesNacional(item.aliquotaIVASimplesNacional?.toString() || '');
            setAliquotaIVAExcecaoSimplesNacional(item.aliquotaIVAExcecaoSimplesNacional?.toString() || '');
            setAliquotaAdicaoBase(item.aliquotaAdicaoBase?.toString() || '');
            setAliquotaReducaoBase(item.aliquotaReducaoBase?.toString() || '');
            setAliquotaIcmsSTOutraUF(item.aliquotaIcmsSTOutraUF?.toString() || '');
            setAliquotaIcmsFreteDiferenciado(item.aliquotaIcmsFreteDiferenciado?.toString() || '');
            setAliquotaIcmsNaoContribuinte(item.aliquotaIcmsNaoContribuinte?.toString() || '');
            setConvenioSubstituicaoTributaria(item.convenioSubstituicaoTributaria || false);
            setAliquotaIcmsDiferencialST(item.aliquotaIcmsDiferencialST?.toString() || '');
            setAliquotaReducaoBaseIcmsST(item.aliquotaReducaoBaseIcmsST?.toString() || '');
            setPercentualFundoPobreza(item.percentualFundoPobreza?.toString() || '');
            setAliquotaIpiSaida(item.aliquotaIpiSaida?.toString() || '');
            setAliquotaIpiEntrada(item.aliquotaIpiEntrada?.toString() || '');
            setAliquotaExcecaoIpiSaida(item.aliquotaExcecaoIpiSaida?.toString() || '');
            setAliquotaExcecaoIpiEntrada(item.aliquotaExcecaoIpiEntrada?.toString() || '');
            setAliquotaII(item.aliquotaII?.toString() || '');
            setAliquotaCofinsSaida(item.aliquotaCofinsSaida?.toString() || '');
            setAliquotaCofinsEntrada(item.aliquotaCofinsEntrada?.toString() || '');
            setAliquotaPISSaida(item.aliquotaPISSaida?.toString() || '');
            setAliquotaPISEntrada(item.aliquotaPISEntrada?.toString() || '');
            setAliquotaCBS(item.aliquotaCBS?.toString() || '');
            setAliquotaIBS(item.aliquotaIBS?.toString() || '');
            setAliquotaIS(item.aliquotaIS?.toString() || '');
            setCodigoEspecificacaoST(item.codigoEspecificacaoST || '');
            setCodigoExcecaoEspecificacaoST(item.codigoExcecaoEspecificacaoST || '');
            setGenero(item.genero || '');
            setMensagemLegal(item.mensagemLegal || '');
        } else {
            resetFormFields();
        }
        setIsModalOpen(true);
    };

    const resetFormFields = () => {
        setNcmCode('');
        setNcmDescription('');
        setControl('Nenhum');
        setUnitOfMeasureId('');
        setExportUnitOfMeasureId('');
        setOriginDestinyRules([]);
        setSituacaoTributaria('00');
        setModalidadeBaseCalculo('3');
        setModalidadeBaseCalculoST('4');
        setInscricaoEstadualST('');
        setAliquotaIcmsProprio('');
        setAliquotaIVA('');
        setAliquotaIVAExcecao('');
        setValidadeIVA('');
        setAliquotaIVASimplesNacional('');
        setAliquotaIVAExcecaoSimplesNacional('');
        setAliquotaAdicaoBase('');
        setAliquotaReducaoBase('');
        setAliquotaIcmsSTOutraUF('');
        setAliquotaIcmsFreteDiferenciado('');
        setAliquotaIcmsNaoContribuinte('');
        setConvenioSubstituicaoTributaria(false);
        setAliquotaIcmsDiferencialST('');
        setAliquotaReducaoBaseIcmsST('');
        setPercentualFundoPobreza('');
        setAliquotaIpiSaida('');
        setAliquotaIpiEntrada('');
        setAliquotaExcecaoIpiSaida('');
        setAliquotaExcecaoIpiEntrada('');
        setAliquotaII('');
        setAliquotaCofinsSaida('');
        setAliquotaCofinsEntrada('');
        setAliquotaPISSaida('');
        setAliquotaPISEntrada('');
        setAliquotaCBS('');
        setAliquotaIBS('');
        setAliquotaIS('');
        setCodigoEspecificacaoST('');
        setCodigoExcecaoEspecificacaoST('');
        setGenero('');
        setMensagemLegal('');
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        const payload = {
            ncmCode, ncmDescription, control, unitOfMeasureId,
            exportUnitOfMeasureId, originDestinyRules,
            situacaoTributaria,
            modalidadeBaseCalculo,
            modalidadeBaseCalculoST,
            inscricaoEstadualST,
            aliquotaIcmsProprio: parseFloat(aliquotaIcmsProprio) || 0,
            aliquotaIVA: parseFloat(aliquotaIVA) || 0,
            aliquotaIVAExcecao: parseFloat(aliquotaIVAExcecao) || 0,
            validadeIVA,
            aliquotaIVASimplesNacional: parseFloat(aliquotaIVASimplesNacional) || 0,
            aliquotaIVAExcecaoSimplesNacional: parseFloat(aliquotaIVAExcecaoSimplesNacional) || 0,
            aliquotaAdicaoBase: parseFloat(aliquotaAdicaoBase) || 0,
            aliquotaReducaoBase: parseFloat(aliquotaReducaoBase) || 0,
            aliquotaIcmsSTOutraUF: parseFloat(aliquotaIcmsSTOutraUF) || 0,
            aliquotaIcmsFreteDiferenciado: parseFloat(aliquotaIcmsFreteDiferenciado) || 0,
            aliquotaIcmsNaoContribuinte: parseFloat(aliquotaIcmsNaoContribuinte) || 0,
            convenioSubstituicaoTributaria,
            aliquotaIcmsDiferencialST: parseFloat(aliquotaIcmsDiferencialST) || 0,
            aliquotaReducaoBaseIcmsST: parseFloat(aliquotaReducaoBaseIcmsST) || 0,
            percentualFundoPobreza: parseFloat(percentualFundoPobreza) || 0,
            aliquotaIpiSaida: parseFloat(aliquotaIpiSaida) || 0,
            aliquotaIpiEntrada: parseFloat(aliquotaIpiEntrada) || 0,
            aliquotaExcecaoIpiSaida: parseFloat(aliquotaExcecaoIpiSaida) || 0,
            aliquotaExcecaoIpiEntrada: parseFloat(aliquotaExcecaoIpiEntrada) || 0,
            aliquotaII: parseFloat(aliquotaII) || 0,
            aliquotaCofinsSaida: parseFloat(aliquotaCofinsSaida) || 0,
            aliquotaCofinsEntrada: parseFloat(aliquotaCofinsEntrada) || 0,
            aliquotaPISSaida: parseFloat(aliquotaPISSaida) || 0,
            aliquotaPISEntrada: parseFloat(aliquotaPISEntrada) || 0,
            aliquotaCBS: parseFloat(aliquotaCBS) || 0,
            aliquotaIBS: parseFloat(aliquotaIBS) || 0,
            aliquotaIS: parseFloat(aliquotaIS) || 0,
            codigoEspecificacaoST,
            codigoExcecaoEspecificacaoST,
            genero,
            mensagemLegal,
            updatedAt: new Date()
        };

        try {
            if (editingItem) {
                await updateDocument(editingItem.id, payload);
                addToast(t('pages.ncm.toasts.updateSuccess'), 'success');
            } else {
                await addDocument({ ...payload, createdAt: new Date() });
                addToast(t('pages.ncm.toasts.createSuccess'), 'success');
            }
            setIsModalOpen(false);
            resetFormFields();
        } catch (err) {
            console.error("Erro ao salvar NCM:", err);
            addToast(t('pages.ncm.toasts.saveError'), 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteDocument(deleteTarget);
            addToast(t('pages.ncm.toasts.deleteSuccess'), 'success');
            setDeleteTarget(null);
        } catch (err) {
            console.error("Erro ao deletar NCM:", err);
            addToast(t('pages.ncm.toasts.deleteError'), 'error');
        }
    };

    const handleAddRule = () => {
        const newRule = {
            id: Date.now(),
            ufOrigem: 'SP',
            ufDestino: 'SP',
            situacaoTributaria: '00',
            utilizacaoEntrada: true,
            utilizacaoSaida: true
        };
        setOriginDestinyRules([...originDestinyRules, newRule]);
    };

    const handleRemoveRule = (id) => {
        setOriginDestinyRules(originDestinyRules.filter(r => r.id !== id));
    };

    const handleRuleChange = (id, field, value) => {
        setOriginDestinyRules(originDestinyRules.map(r => r.id === id ? { ...r, [field]: value } : r));
    };

    return (
        <div className={styles.pageContainer}>
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            <PageHeader
                title={t('pages.ncm.title')}
                actionText={can.create ? t('pages.ncm.newNcm') : null}
                onAction={() => handleOpenModal()}
                showAction={can.create}
                actionExtra={
                    can.import && (
                        <button
                            onClick={() => setIsImportOpen(true)}
                            className={styles.cancelButton}
                            title={t('pages.ncm.importJson')}
                        >
                            <BsFiletypeJson />
                        </button>
                    )
                }
            />

            <div className={styles.searchBar}>
                <SearchBar
                    placeholder={t('pages.ncm.searchPlaceholder')}
                    onSearch={(term) => {
                        setSearchTerm(term);
                        setCurrentPage(1);
                    }}
                    initialValue={searchTerm}
                />
            </div>

            {loading ? (
                <TableSkeleton columns={3} rows={ITEMS_PER_PAGE} />
            ) : paginatedNCM.length > 0 ? (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t('pages.ncm.table.code')}</th>
                                <th>{t('pages.ncm.table.description')}</th>
                                <th style={{ width: '150px' }}>{t('pages.ncm.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedNCM.map(item => (
                                <tr key={item.id}>
                                    <td><strong>{item.ncmCode}</strong></td>
                                    <td>{item.ncmDescription}</td>
                                    <td>
                                        {can.edit && (
                                            <button onClick={() => handleOpenModal(item)} className={styles.actionButton} title={t('common.edit')}>
                                                <FiEdit />
                                            </button>
                                        )}
                                        {can.delete && (
                                            <button onClick={() => setDeleteTarget(item.id)} className={`${styles.actionButton} ${styles.deleteButton}`} title={t('common.delete')}>
                                                <FiTrash2 />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <EmptyState
                    title={t('pages.ncm.noNcmFound')}
                    message={searchTerm ? t('pages.ncm.noResultsForSearch') : t('pages.ncm.emptyStateMessage')}
                    actionText={can.create ? t('pages.ncm.newNcm') : null}
                    onAction={() => handleOpenModal()}
                />
            )}

            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredNCM.length}
                    onPageChange={setCurrentPage}
                    itemsLabel={t('common.items')}
                />
            )}

            {/* ── Import Modal ── */}
            {isImportOpen && (
                <ImportModal
                    onClose={() => setIsImportOpen(false)}
                    existingCodes={existingCodes}
                    addDocument={addDocument}
                    t={t}
                />
            )}

            {/* ── Delete Confirm Modal ── */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title={t('pages.ncm.confirmDeleteTitle')}
                message={t('pages.ncm.confirmDeleteMessage')}
                confirmText={t('common.delete')}
                danger={true}
            />

            {/* ── Edit / Create Modal with Tabs ── */}
            <FormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSave}
                title={editingItem ? t('pages.ncm.editNcm') : t('pages.ncm.newNcm')}
                isLoading={formLoading}
                submitText={formLoading ? t('pages.ncm.form.saving') : t('pages.ncm.form.saveNcm')}
                maxWidth="1200px"
            >
                {/* Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid #ddd', padding: '0 20px' }}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('geral')}
                        style={{
                            padding: '12px 20px',
                            border: 'none',
                            background: activeTab === 'geral' ? 'var(--color-primary, #4a90e2)' : 'transparent',
                            color: activeTab === 'geral' ? 'white' : 'var(--color-text, #333)',
                            cursor: 'pointer',
                            borderRadius: '8px 8px 0 0',
                            fontWeight: 500
                        }}
                    >
                        {t('pages.ncm.tabs.geral')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('icms')}
                        style={{
                            padding: '12px 20px',
                            border: 'none',
                            background: activeTab === 'icms' ? 'var(--color-primary, #4a90e2)' : 'transparent',
                            color: activeTab === 'icms' ? 'white' : 'var(--color-text, #333)',
                            cursor: 'pointer',
                            borderRadius: '8px 8px 0 0',
                            fontWeight: 500
                        }}
                    >
                        {t('pages.ncm.tabs.icms')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('federal')}
                        style={{
                            padding: '12px 20px',
                            border: 'none',
                            background: activeTab === 'federal' ? 'var(--color-primary, #4a90e2)' : 'transparent',
                            color: activeTab === 'federal' ? 'white' : 'var(--color-text, #333)',
                            cursor: 'pointer',
                            borderRadius: '8px 8px 0 0',
                            fontWeight: 500
                        }}
                    >
                        {t('pages.ncm.tabs.federal')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('regras')}
                        style={{
                            padding: '12px 20px',
                            border: 'none',
                            background: activeTab === 'regras' ? 'var(--color-primary, #4a90e2)' : 'transparent',
                            color: activeTab === 'regras' ? 'white' : 'var(--color-text, #333)',
                            cursor: 'pointer',
                            borderRadius: '8px 8px 0 0',
                            fontWeight: 500
                        }}
                    >
                        {t('pages.ncm.tabs.regras')}
                    </button>
                </div>

                {/* TAB 1: DADOS GERAIS */}
                {activeTab === 'geral' && (
                    <div>
                        <fieldset className={styles.fieldset}>
                            <legend>{t('pages.ncm.form.generalData')}</legend>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.ncmCode')} *</label>
                                    <input type="text" className={styles.input} value={ncmCode} onChange={e => setNcmCode(e.target.value)} required />
                                </div>
                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.control')}</label>
                                    <select className={styles.select} value={control} onChange={e => setControl(e.target.value)}>
                                        {CONTROLE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                </div>
                                <div className={styles.formGroupFull}>
                                    <label>{t('pages.ncm.form.description')} *</label>
                                    <textarea className={styles.textarea} value={ncmDescription} onChange={e => setNcmDescription(e.target.value)} required rows={3} />
                                </div>
                            </div>
                        </fieldset>

                        <fieldset className={styles.fieldset}>
                            <legend>{t('pages.ncm.form.unitOfMeasure')}</legend>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.unitOfMeasureDefault')}</label>
                                    <select className={styles.select} value={unitOfMeasureId} onChange={e => setUnitOfMeasureId(e.target.value)}>
                                        <option value="">{t('pages.ncm.form.selectUnit')}</option>
                                        {unitOfMeasures?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.unitOfMeasureExport')}</label>
                                    <select className={styles.select} value={exportUnitOfMeasureId} onChange={e => setExportUnitOfMeasureId(e.target.value)}>
                                        <option value="">{t('pages.ncm.form.selectUnit')}</option>
                                        {unitOfMeasures?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                )}

                {/* TAB 2: TRIBUTAÇÃO ICMS */}
                {activeTab === 'icms' && (
                    <div>
                        <fieldset className={styles.fieldset}>
                            <legend>{t('pages.ncm.form.icmsConfig')}</legend>

                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.situacaoTributaria')}</label>
                                    <select className={styles.select} value={situacaoTributaria} onChange={e => setSituacaoTributaria(e.target.value)}>
                                        {SITUACAO_TRIBUTARIA_OPTIONS.map(opt => (
                                            <option key={opt.code} value={opt.code}>{opt.code} - {opt.description}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.modalidadeBaseCalculo')}</label>
                                    <select className={styles.select} value={modalidadeBaseCalculo} onChange={e => setModalidadeBaseCalculo(e.target.value)}>
                                        {MODALIDADE_BASE_CALCULO_OPTIONS.map(opt => (
                                            <option key={opt.code} value={opt.code}>{opt.description}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.modalidadeBaseCalculoST')}</label>
                                    <select className={styles.select} value={modalidadeBaseCalculoST} onChange={e => setModalidadeBaseCalculoST(e.target.value)}>
                                        {MODALIDADE_BASE_CALCULO_ST_OPTIONS.map(opt => (
                                            <option key={opt.code} value={opt.code}>{opt.description}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.inscricaoEstadualST')}</label>
                                    <input type="text" className={styles.input} value={inscricaoEstadualST} onChange={e => setInscricaoEstadualST(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIcmsProprio')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIcmsProprio} onChange={e => setAliquotaIcmsProprio(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIVA')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIVA} onChange={e => setAliquotaIVA(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIVAExcecao')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIVAExcecao} onChange={e => setAliquotaIVAExcecao(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.validadeIVA')}</label>
                                    <input type="date" className={styles.input} value={validadeIVA} onChange={e => setValidadeIVA(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIVASimplesNacional')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIVASimplesNacional} onChange={e => setAliquotaIVASimplesNacional(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIVAExcecaoSimplesNacional')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIVAExcecaoSimplesNacional} onChange={e => setAliquotaIVAExcecaoSimplesNacional(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaAdicaoBase')}</label>
                                    <input type="number" step="0.0001" className={styles.input} value={aliquotaAdicaoBase} onChange={e => setAliquotaAdicaoBase(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaReducaoBase')}</label>
                                    <input type="number" step="0.0001" className={styles.input} value={aliquotaReducaoBase} onChange={e => setAliquotaReducaoBase(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIcmsSTOutraUF')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIcmsSTOutraUF} onChange={e => setAliquotaIcmsSTOutraUF(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIcmsFreteDiferenciado')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIcmsFreteDiferenciado} onChange={e => setAliquotaIcmsFreteDiferenciado(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIcmsNaoContribuinte')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIcmsNaoContribuinte} onChange={e => setAliquotaIcmsNaoContribuinte(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkboxLabel}>
                                        <input type="checkbox" checked={convenioSubstituicaoTributaria} onChange={e => setConvenioSubstituicaoTributaria(e.target.checked)} />
                                        <span>{t('pages.ncm.form.convenioSubstituicaoTributaria')}</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIcmsDiferencialST')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIcmsDiferencialST} onChange={e => setAliquotaIcmsDiferencialST(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaReducaoBaseIcmsST')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaReducaoBaseIcmsST} onChange={e => setAliquotaReducaoBaseIcmsST(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.percentualFundoPobreza')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={percentualFundoPobreza} onChange={e => setPercentualFundoPobreza(e.target.value)} />
                                </div>
                            </div>
                        </fieldset>
                    </div>
                )}

                {/* TAB 3: TRIBUTAÇÃO FEDERAL */}
                {activeTab === 'federal' && (
                    <div>
                        <fieldset className={styles.fieldset}>
                            <legend>{t('pages.ncm.form.federalTax')}</legend>

                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIpiSaida')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIpiSaida} onChange={e => setAliquotaIpiSaida(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIpiEntrada')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIpiEntrada} onChange={e => setAliquotaIpiEntrada(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaExcecaoIpiSaida')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaExcecaoIpiSaida} onChange={e => setAliquotaExcecaoIpiSaida(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaExcecaoIpiEntrada')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaExcecaoIpiEntrada} onChange={e => setAliquotaExcecaoIpiEntrada(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaII')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaII} onChange={e => setAliquotaII(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaCofinsSaida')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaCofinsSaida} onChange={e => setAliquotaCofinsSaida(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaCofinsEntrada')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaCofinsEntrada} onChange={e => setAliquotaCofinsEntrada(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaPISSaida')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaPISSaida} onChange={e => setAliquotaPISSaida(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaPISEntrada')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaPISEntrada} onChange={e => setAliquotaPISEntrada(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaCBS')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaCBS} onChange={e => setAliquotaCBS(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIBS')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIBS} onChange={e => setAliquotaIBS(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.aliquotaIS')}</label>
                                    <input type="number" step="0.01" className={styles.input} value={aliquotaIS} onChange={e => setAliquotaIS(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.codigoEspecificacaoST')}</label>
                                    <input type="text" className={styles.input} value={codigoEspecificacaoST} onChange={e => setCodigoEspecificacaoST(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.codigoExcecaoEspecificacaoST')}</label>
                                    <input type="text" className={styles.input} value={codigoExcecaoEspecificacaoST} onChange={e => setCodigoExcecaoEspecificacaoST(e.target.value)} />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t('pages.ncm.form.genero')}</label>
                                    <select className={styles.select} value={genero} onChange={e => setGenero(e.target.value)}>
                                        {GENERO_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroupFull}>
                                    <label>{t('pages.ncm.form.mensagemLegal')}</label>
                                    <textarea className={styles.textarea} value={mensagemLegal} onChange={e => setMensagemLegal(e.target.value)} rows={3} />
                                </div>
                            </div>
                        </fieldset>
                    </div>
                )}

                {/* TAB 4: REGRAS ORIGEM/DESTINO */}
                {activeTab === 'regras' && (
                    <fieldset className={styles.fieldset}>
                        <legend>{t('pages.ncm.form.originDestinyRules')}</legend>
                        <div className={ncmStyles.ruleHeader}>
                            <button type="button" onClick={handleAddRule} className={styles.addButton}>
                                {t('pages.ncm.form.addRule')}
                            </button>
                        </div>
                        <div className={ncmStyles.rulesList}>
                            {originDestinyRules.map((rule) => (
                                <div key={rule.id} className={ncmStyles.ruleCard}>
                                    <div className={ncmStyles.ruleGrid}>
                                        <div className={styles.formGroup}>
                                            <label>{t('pages.ncm.form.origin')}</label>
                                            <select className={styles.select} value={rule.ufOrigem} onChange={e => handleRuleChange(rule.id, 'ufOrigem', e.target.value)}>
                                                {UF_OPTIONS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                                            </select>
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label>{t('pages.ncm.form.destiny')}</label>
                                            <select className={styles.select} value={rule.ufDestino} onChange={e => handleRuleChange(rule.id, 'ufDestino', e.target.value)}>
                                                {UF_OPTIONS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                                            </select>
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label>{t('pages.ncm.form.situacaoTributariaST')}</label>
                                            <select className={styles.select} value={rule.situacaoTributaria} onChange={e => handleRuleChange(rule.id, 'situacaoTributaria', e.target.value)}>
                                                {ST_OPTIONS.map(st => <option key={st.code} value={st.code}>{st.code} - {st.description}</option>)}
                                            </select>
                                        </div>
                                        <div className={ncmStyles.checkboxContainer}>
                                            <label><input type="checkbox" checked={rule.utilizacaoEntrada} onChange={e => handleRuleChange(rule.id, 'utilizacaoEntrada', e.target.checked)} /> {t('pages.ncm.form.entrada')}</label>
                                            <label><input type="checkbox" checked={rule.utilizacaoSaida} onChange={e => handleRuleChange(rule.id, 'utilizacaoSaida', e.target.checked)} /> {t('pages.ncm.form.saida')}</label>
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => handleRemoveRule(rule.id)} className={ncmStyles.removeRuleBtn}>
                                        {t('pages.ncm.form.removeRule')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </fieldset>
                )}
            </FormModal>
        </div>
    );
};

export default NCMRegisterPage;